'use client'
import { FlightTimingSlot } from '@/src/hooks/srp/flightTimingFilterManager'
import { Box, Button, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import React from 'react'

interface FlightTimingsFilterProps {
    flightTimings: FlightTimingSlot[]
    onTimeSlotSelect: (slotId: string) => void
    onReset: () => void
    shouldShowReset: boolean
    title: string
}

const FlightTimingsFilter: React.FC<FlightTimingsFilterProps> = ({
    flightTimings,
    onTimeSlotSelect,
    onReset,
    shouldShowReset,
    title
}) => {
    const t = useTranslations('srpFilters')
    return (
        <Box
            sx={{
                p: 2,
                borderBottom: 1,
                borderColor: 'grey.200',
                '&:last-child': {
                    borderBottom: 0
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'grey.900' }}>
                    {title}
                </Typography>
                {shouldShowReset && (
                    <Button
                        variant="text"
                        size="small"
                        onClick={onReset}
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: 'brand.primary',
                            p: 0,
                            minWidth: 'auto',
                            '&:hover': {
                                bgcolor: 'transparent',
                                color: 'brand.primaryHover'
                            }
                        }}
                    >
                        {t('reset')}
                    </Button>
                )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Departing flight section */}
                <Box>
                    {/* <Typography
                        variant="caption"
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: 'grey.700',
                            mb: 1,
                            display: 'block',
                            letterSpacing: '0.025em'
                        }}
                    >
                        {flightTimings.title}{' '}
                        <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 400, color: 'grey.400' }}>
                            {flightTimings.route}
                        </Typography>
                    </Typography> */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                        {flightTimings?.map(slot => (
                            <Button
                                key={slot.value}
                                variant={slot.isSelected ? 'contained' : 'outlined'}
                                onClick={() => onTimeSlotSelect(slot.value)}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: 0.75,
                                    borderRadius: 1,
                                    transition: 'all 0.2s',
                                    minHeight: 44,
                                    ...(slot.isSelected
                                        ? {
                                              bgcolor: 'primary.100',
                                              borderColor: 'link.main',
                                              color: 'link.main',
                                              border: '1px solid',
                                              boxShadow: 'none'
                                          }
                                        : {
                                              borderColor: 'grey.200',
                                              bgcolor: 'background.default',
                                              color: 'grey.900',
                                              '&:hover': {
                                                  borderColor: 'grey.300',
                                                  bgcolor: 'grey.50'
                                              }
                                          })
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        justifyContent: 'center',
                                        // border: '1px solid red',
                                        width: '100%'
                                    }}
                                >
                                    {slot.icon && (
                                        <slot.icon
                                            fontSize={'small'}
                                            color={slot.isSelected ? 'primary' : 'disabled'}
                                        />
                                    )}
                                </Box>

                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        textAlign: 'center',
                                        lineHeight: 1.2,
                                        textTransform: 'none',
                                        mt: 0.5
                                    }}
                                >
                                    {slot.label}
                                </Typography>
                            </Button>
                        ))}
                    </Box>
                </Box>

                {/* Returning flight section */}
                {/* <Box>
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: 'grey.700',
                            mb: 1,
                            display: 'block',
                            letterSpacing: '0.025em'
                        }}
                    >
                        {returningFlight.title}{' '}
                        <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 400, color: 'grey.400' }}>
                            {returningFlight.route}
                        </Typography>
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                        {returningFlight.timeSlots.map(slot => (
                            <Button
                                key={slot.id}
                                variant={slot.isSelected ? 'contained' : 'outlined'}
                                onClick={() => onTimeSlotSelect('returning', slot.id)}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    p: 0.75,
                                    borderRadius: 1,
                                    transition: 'all 0.2s',
                                    minHeight: 44,
                                    ...(slot.isSelected
                                        ? {
                                              bgcolor: 'rgba(42, 82, 152, 0.08)',
                                              borderColor: 'brand.primary',
                                              color: 'brand.primary',
                                              '&:hover': {
                                                  bgcolor: 'rgba(42, 82, 152, 0.12)',
                                                  borderColor: 'brand.primary'
                                              }
                                          }
                                        : {
                                              borderColor: 'grey.200',
                                              bgcolor: 'background.default',
                                              color: 'grey.900',
                                              '&:hover': {
                                                  borderColor: 'grey.300',
                                                  bgcolor: 'grey.50'
                                              }
                                          })
                                }}
                            >
                                <TimeSlotIcon type={slot.icon} />

                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        textAlign: 'center',
                                        lineHeight: 1.2,
                                        textTransform: 'none'
                                    }}
                                >
                                    {slot.label}
                                </Typography>
                            </Button>
                        ))}
                    </Box>
                </Box> */}
            </Box>
        </Box>
    )
}

export { FlightTimingsFilter }
